// src/pages/userProfile/UmowyTab.js
import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, message, Popconfirm, Spin, Tag, Upload } from 'antd';
import { FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { collection, getDocs } from 'firebase/firestore';
import moment from 'moment';
import { useFirebase } from '../../FirebaseContext';
import { FIRM_DETAILS, hasFirmDetails } from '../../services/firmDetails';
import { generateDocumentsForUser, uploadSignedContract, recomputeAllSigned } from '../../services/contractsService';

const tsToStr = (ts) => (ts && ts.toDate ? moment(ts.toDate()).format('DD-MM-YYYY') : 'Brak');

const UmowyTab = ({ userId }) => {
  const { currentFirebase, currentProject } = useFirebase();
  const firestore = currentFirebase.firestore;
  const storage = currentFirebase.storage;
  const firm = FIRM_DETAILS[currentProject];

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, `users/${userId}/umowy`));
      setContracts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      message.error('Błąd ładowania umów');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const handleGenerate = async () => {
    setGenerating(true);
    const hide = message.loading('Generowanie dokumentów (PDF)...', 0);
    try {
      const res = await generateDocumentsForUser({ firestore, storage, userId, firm });
      hide();
      if (res.reason === 'no_templates') message.warning('Brak szablonów (documentTemplates) w tym projekcie');
      else message.success(`Wygenerowano ${res.generated} dokument(ów)`);
      await load();
    } catch (e) {
      hide();
      message.error(`Błąd generowania: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadSigned = async (contract, file) => {
    setUploadingId(contract.id);
    const hide = message.loading('Wgrywanie podpisanego dokumentu...', 0);
    try {
      await uploadSignedContract({ firestore, storage, userId, contractId: contract.id, file, existingPdfUrl: contract.pdfUrl });
      await recomputeAllSigned({ firestore, userId });
      hide();
      message.success('Dokument oznaczony jako podpisany');
      await load();
    } catch (e) {
      hide();
      message.error(`Błąd wgrywania: ${e.message}`);
    } finally {
      setUploadingId(null);
    }
    return false; // не давать antd Upload грузить самому
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Popconfirm
          title="Wygenerować dokumenty?"
          description="Istniejące dokumenty o tych samych nazwach zostaną nadpisane."
          onConfirm={handleGenerate}
          okText="Tak"
          cancelText="Nie"
          disabled={!hasFirmDetails(firm)}
        >
          <Button type="primary" loading={generating} disabled={!hasFirmDetails(firm)}>
            Generuj dokumenty
          </Button>
        </Popconfirm>
        {!hasFirmDetails(firm) && (
          <Alert style={{ marginTop: 8 }} type="warning" showIcon message={`Brak danych firmy dla projektu ${currentProject} — generowanie wyłączone`} />
        )}
      </div>

      {loading ? <Spin /> : contracts.length === 0 ? (
        <span style={{ color: '#9ca3af' }}>Brak umów</span>
      ) : contracts.map((c) => (
        <Card key={c.id} size="small" style={{ marginBottom: 10, borderRadius: 8 }}
          title={<>{c.documentType || c.id} {c.signed ? <Tag color="green">Podpisany</Tag> : <Tag color="orange">{c.status || 'Oczekuje na podpis'}</Tag>}{c.signedByAdmin && <Tag color="blue">admin</Tag>}</>}>
          <p><strong>Data generacji:</strong> {tsToStr(c.dataGeneracji)}</p>
          <p><strong>Data ważności:</strong> {tsToStr(c.dataWaznosci)}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {c.pdfUrl && <Button size="small" icon={<FilePdfOutlined />} href={c.pdfUrl} target="_blank" rel="noreferrer">PDF</Button>}
            <Upload showUploadList={false} accept=".pdf,image/*" beforeUpload={(file) => handleUploadSigned(c, file)}>
              <Button size="small" icon={<UploadOutlined />} loading={uploadingId === c.id}>Wgraj podpisany dokument</Button>
            </Upload>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default UmowyTab;
